import { ActiveElement, Chart, TooltipItem, TooltipModel, TooltipOptions } from "chart.js";
import { themeProps } from "../../../theme";
import { getViewportTransform, mapPointToViewport } from "../selection-zoom/geometry";

export type TExternalTooltipChartType = "line" | "radar";

export interface IExternalTooltipBodyLine {
  text: string;
  color?: string | null;
}

export interface IExternalTooltipContent {
  titleLines?: string[];
  bodyLines?: Array<string | IExternalTooltipBodyLine>;
}

export type TExternalTooltipContentFormatter<TType extends TExternalTooltipChartType = TExternalTooltipChartType> = (
  dataPoints: TooltipItem<TType>[],
) => IExternalTooltipContent | null | undefined;

export type TExternalTooltipDisplayMode = "floating" | "header";

interface IExtendedTooltipOptions extends TooltipOptions<TExternalTooltipChartType> {
  unitsX?: string;
  unitsY?: string;
  xOffset?: number;
  yOffset?: number;
  displayMode?: TExternalTooltipDisplayMode;
  onHeaderContentChange?: (content: IExternalTooltipContent | null) => void;
  contentFormatter?: TExternalTooltipContentFormatter;
  contentDecorator?: (
    content: IExternalTooltipContent,
    dataPoints: TooltipItem<TExternalTooltipChartType>[],
    chart: IChartWithCrosshair,
  ) => IExternalTooltipContent;
  titleFormatter?: (dataPoint: TooltipItem<TExternalTooltipChartType>) => string | undefined;
  xValueFormatter?: (value: number, dataPoint: TooltipItem<TExternalTooltipChartType>) => string | null | undefined;
  yValueFormatter?: (value: number, dataPoint: TooltipItem<TExternalTooltipChartType>) => string | null | undefined;
}

interface ISelectionZoomTooltipOptions {
  viewportMode?: boolean;
  viewport?: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
}

type CrosshairState = {
  locked?: boolean;
  lockedX?: number | null;
  lockedActiveElements?: ActiveElement[] | null;
  snappedActiveElements?: ActiveElement[] | null;
  x?: number | null;
  enabled?: boolean;
  baseValue?: number | null;
  baseDatasetIndex?: number | null;
  snapPixelY?: number | null;
  markerValue?: number | null;
  markerDatasetIndex?: number | null;
  syncCursorValues?: readonly (number | null)[] | null;
  syncMarkerValues?: readonly (number | null)[] | null;
  syncDatasetRanges?: readonly { datasetIndex: number; min: number; max: number }[] | null;
  syncMarkerInRange?: boolean;
  syncedPoint?: { datasetIndex: number; nativeX: number; x: number; y: number } | null;
  syncBand?: { datasetIndex: number; min: number; max: number } | null;
};

type TooltipController = TooltipModel<TExternalTooltipChartType> & {
  setActiveElements?(active: ActiveElement[], eventPosition: { x: number; y: number }): void;
  _active?: ActiveElement[];
  update?(change?: boolean): void;
};

export interface IChartWithCrosshair extends Chart<TExternalTooltipChartType> {
  crosshair?: CrosshairState;
  _lockedTooltip?: TooltipModel<TExternalTooltipChartType> | null;
  _externalTooltipPoint?: { x: number; y: number } | null;
  _externalTooltipEl?: HTMLDivElement | null;
}

const VIEWPORT_EDGE_PADDING = 8;
const TOOLTIP_OFFSET = 10;
const TOOLTIP_Z_INDEX = 1400;

const DEGREE_UNITS = "\u00B0";
const DATE_TIME_LABEL_REGEX = /^\d{2}\.\d{2}\.\d{2}\/\d{2}:\d{2}:\d{2}$/;
const DATE_LABEL_REGEX = /^\d{2}\.\d{2}\.\d{2}(\/\d{2}:\d{2}:\d{2})?$/;

const formatTooltipNumber = (value: number) => value.toFixed(2);

const normalizeBodyLine = (line: string | IExternalTooltipBodyLine): IExternalTooltipBodyLine => {
  return typeof line === "string" ? { text: line } : line;
};

export const formatExternalTooltipHeaderText = (content: IExternalTooltipContent): string => {
  const parts: string[] = [];

  content.titleLines?.forEach(line => {
    const trimmed = line.trim();
    if (trimmed) {
      parts.push(trimmed);
    }
  });

  content.bodyLines?.forEach(line => {
    const text = typeof line === "string" ? line : line.text;
    const trimmed = text.trim();
    if (trimmed) {
      parts.push(trimmed);
    }
  });

  return parts.join("  ");
};

const getChartTooltipOptions = (chart: Chart): IExtendedTooltipOptions | undefined =>
  chart.config.options?.plugins?.tooltip as IExtendedTooltipOptions | undefined;

const hideFloatingTooltipElement = (chart: IChartWithCrosshair): void => {
  const tooltipEl = chart._externalTooltipEl;
  if (tooltipEl) {
    tooltipEl.style.opacity = "0";
    tooltipEl.style.display = "none";
  }
};

const appendUnits = (value: string, units?: string) => {
  if (!units) {
    return value;
  }

  const separatorIndex = value.indexOf(";");

  if (separatorIndex === -1) {
    return `${value} ${units}`;
  }

  const primaryValue = value.slice(0, separatorIndex).trimEnd();
  const suffix = value.slice(separatorIndex);

  return `${primaryValue} ${units}${suffix}`;
};

const parseTooltipNumber = (value: string | number | undefined | null) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  const parsedValue = Number(value.replace(/\s/g, "").replace(/,/g, "."));
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const getParsedTooltipAxisValue = (dataPoint: TooltipItem<TExternalTooltipChartType>, axis: "x" | "y") => {
  const parsedPoint = dataPoint.parsed as { x?: unknown; y?: unknown };
  const parsedValue = axis === "x" ? parsedPoint.x : parsedPoint.y;

  if (typeof parsedValue === "number" && Number.isFinite(parsedValue)) {
    return parsedValue;
  }

  if (typeof dataPoint.raw === "object" && dataPoint.raw !== null) {
    const rawValue = (dataPoint.raw as Record<string, unknown>)[axis];

    if (typeof rawValue === "number" && Number.isFinite(rawValue)) {
      return rawValue;
    }
  }

  if (axis === "y" && typeof dataPoint.raw === "number" && Number.isFinite(dataPoint.raw)) {
    return dataPoint.raw;
  }

  return null;
};

const getTooltipXValue = (dataPoint: TooltipItem<TExternalTooltipChartType>, xOffset: number) => {
  const parsedValue = getParsedTooltipAxisValue(dataPoint, "x");

  if (parsedValue !== null) {
    return parsedValue - xOffset * dataPoint.datasetIndex;
  }

  const labelValue = parseTooltipNumber(dataPoint.label);
  return labelValue !== null ? labelValue - xOffset * dataPoint.datasetIndex : null;
};

const getTooltipYValue = (dataPoint: TooltipItem<TExternalTooltipChartType>, yOffset: number, isDate: boolean) => {
  const parsedValue = getParsedTooltipAxisValue(dataPoint, "y");
  const baseValue = parsedValue ?? parseTooltipNumber(dataPoint.formattedValue);

  if (baseValue === null) {
    return null;
  }

  return isDate ? baseValue : baseValue - yOffset * dataPoint.datasetIndex;
};

const formatTooltipValue = (
  value: number,
  dataPoint: TooltipItem<TExternalTooltipChartType>,
  formatter?: (value: number, dataPoint: TooltipItem<TExternalTooltipChartType>) => string | null | undefined,
) => {
  const formattedValue = formatter?.(value, dataPoint);

  if (typeof formattedValue === "string" && formattedValue !== "") {
    return formattedValue;
  }

  return formatTooltipNumber(value);
};

const getViewportTooltipPoint = (chart: IChartWithCrosshair, x: number, y: number): { x: number; y: number } => {
  const pluginOptions = chart.config.options?.plugins as
    | (Record<string, unknown> & {
        selectionZoom?: ISelectionZoomTooltipOptions;
      })
    | undefined;
  const selectionZoomOptions = pluginOptions?.selectionZoom;

  if (!selectionZoomOptions?.viewportMode) {
    return { x, y };
  }

  const transform = getViewportTransform(
    {
      left: 0,
      right: chart.width,
      top: 0,
      bottom: chart.height,
    },
    selectionZoomOptions.viewport,
  );

  return transform ? mapPointToViewport({ x, y }, transform) : { x, y };
};

const computeViewportTooltipPosition = (
  cursorX: number,
  cursorY: number,
  tooltipWidth: number,
  tooltipHeight: number,
): { left: number; top: number; transform: string } => {
  const maxLeft = window.innerWidth - tooltipWidth - VIEWPORT_EDGE_PADDING;
  const maxTop = window.innerHeight - tooltipHeight - VIEWPORT_EDGE_PADDING;

  let left: number;
  let transform: string;

  if (cursorX - tooltipWidth / 2 < VIEWPORT_EDGE_PADDING) {
    left = VIEWPORT_EDGE_PADDING;
    transform = "none";
  } else if (cursorX + tooltipWidth / 2 > window.innerWidth - VIEWPORT_EDGE_PADDING) {
    left = maxLeft;
    transform = "none";
  } else {
    left = cursorX;
    transform = "translateX(-50%)";
  }

  const showAbove = cursorY + TOOLTIP_OFFSET + tooltipHeight > window.innerHeight - VIEWPORT_EDGE_PADDING;
  let top = showAbove ? cursorY - tooltipHeight - TOOLTIP_OFFSET : cursorY + TOOLTIP_OFFSET;

  if (top < VIEWPORT_EDGE_PADDING) {
    top = cursorY + TOOLTIP_OFFSET;
  }

  return {
    left: Math.max(VIEWPORT_EDGE_PADDING, Math.min(left, maxLeft)),
    top: Math.max(VIEWPORT_EDGE_PADDING, Math.min(top, maxTop)),
    transform,
  };
};

export const getOrCreateTooltip = (chart: IChartWithCrosshair) => {
  let tooltipEl = chart._externalTooltipEl ?? null;

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.style.alignItems = "flex-start";
    tooltipEl.style.background = "rgba(0, 0, 0, 0.7)";
    tooltipEl.style.borderRadius = "3px";
    tooltipEl.style.color = "white";
    tooltipEl.style.display = "flex";
    tooltipEl.style.justifyContent = "flex-start";
    tooltipEl.style.minWidth = "250px";
    tooltipEl.style.opacity = "1";
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.position = "fixed";
    tooltipEl.style.transition = "opacity .1s ease";
    tooltipEl.style.zIndex = String(TOOLTIP_Z_INDEX);

    const table = document.createElement("table");
    table.style.margin = "0px";
    table.style.width = "100%";
    table.style.textAlign = "left";

    tooltipEl.appendChild(table);
    document.body.appendChild(tooltipEl);
    chart._externalTooltipEl = tooltipEl;
  }

  if (typeof themeProps.options?.typography === "object") {
    tooltipEl.style.fontFamily = themeProps.options.typography.fontFamily ?? "";
  }

  return tooltipEl;
};

const isPointerLeaveEvent = (eventType: string | undefined): boolean =>
  eventType === "mouseout" || eventType === "mouseleave";

export const hideExternalTooltip = (
  chart: IChartWithCrosshair | null | undefined,
  options?: { force?: boolean },
): void => {
  if (!chart) {
    return;
  }

  if (chart.crosshair?.locked && !options?.force) {
    return;
  }

  chart._lockedTooltip = null;
  chart._externalTooltipPoint = null;

  if (chart.crosshair) {
    chart.crosshair.locked = false;
    chart.crosshair.lockedX = null;
    chart.crosshair.lockedActiveElements = null;
    chart.crosshair.enabled = false;
    chart.crosshair.x = null;
    chart.crosshair.snappedActiveElements = null;
  }

  if (typeof chart.setActiveElements === "function") {
    chart.setActiveElements([]);
  }

  const tooltip = chart.tooltip as TooltipController | undefined;
  if (tooltip) {
    if (typeof tooltip.setActiveElements === "function") {
      tooltip.setActiveElements([], { x: 0, y: 0 });
    } else {
      tooltip._active = [];
    }

    if (typeof tooltip.update === "function") {
      tooltip.update(true);
    }
  }

  hideFloatingTooltipElement(chart);
  getChartTooltipOptions(chart)?.onHeaderContentChange?.(null);
};

export const ExternalTooltipCleanupPlugin = {
  id: "externalTooltipCleanup",
  beforeDestroy(chart: IChartWithCrosshair) {
    hideExternalTooltip(chart, { force: true });
    chart._externalTooltipEl?.remove();
    chart._externalTooltipEl = null;
  },
  afterEvent(chart: IChartWithCrosshair, args: { event?: { type?: string } }) {
    if (isPointerLeaveEvent(args.event?.type) && !chart.crosshair?.locked) {
      hideExternalTooltip(chart);
    }
  },
};

export const ExternalTooltip = <TType extends TExternalTooltipChartType>(context: {
  chart: Chart<TType>;
  tooltip: TooltipModel<TType>;
}) => {
  const chart = context.chart as unknown as IChartWithCrosshair;
  const tooltip = context.tooltip as unknown as TooltipModel<TExternalTooltipChartType>;
  const crosshair = chart.crosshair;

  if (!crosshair?.locked) {
    chart._lockedTooltip = null;
  }

  // Use the stored tooltip while the crosshair stays locked.
  let model: TooltipModel<TExternalTooltipChartType> = tooltip;

  if (crosshair?.locked) {
    const lockedX = crosshair.lockedX ?? crosshair.x ?? null;
    const tooltipMatchesLock =
      lockedX == null || !Number.isFinite(tooltip.caretX) || Math.abs(tooltip.caretX - lockedX) <= 1;

    if (tooltip?.dataPoints?.length && tooltipMatchesLock) {
      chart._lockedTooltip = tooltip;
      model = tooltip;
    } else {
      const stored = chart._lockedTooltip;
      const storedMatchesLock =
        stored && lockedX != null && Number.isFinite(stored.caretX) && Math.abs(stored.caretX - lockedX) <= 1;

      if (stored?.dataPoints?.length && storedMatchesLock) {
        model = stored;
      } else if (tooltip?.dataPoints?.length) {
        model = tooltip;
      }
    }
  }

  const tooltipEl = getOrCreateTooltip(chart);
  const modelOptions = model.options as IExtendedTooltipOptions;
  const chartConfigOptions = getChartTooltipOptions(chart);
  const isHeaderMode = chartConfigOptions?.displayMode === "header";

  const unitsX = modelOptions.unitsX ?? chartConfigOptions?.unitsX;
  const unitsY = modelOptions.unitsY ?? chartConfigOptions?.unitsY;
  const xOffset = modelOptions.xOffset ?? chartConfigOptions?.xOffset ?? 0;
  const yOffset = modelOptions.yOffset ?? chartConfigOptions?.yOffset ?? 0;
  const contentFormatter =
    typeof chartConfigOptions?.contentFormatter === "function" ? chartConfigOptions.contentFormatter : undefined;
  const contentDecorator =
    typeof chartConfigOptions?.contentDecorator === "function" ? chartConfigOptions.contentDecorator : undefined;
  const titleFormatter =
    typeof chartConfigOptions?.titleFormatter === "function" ? chartConfigOptions.titleFormatter : undefined;
  const xValueFormatter =
    typeof chartConfigOptions?.xValueFormatter === "function" ? chartConfigOptions.xValueFormatter : undefined;
  const yValueFormatter =
    typeof chartConfigOptions?.yValueFormatter === "function" ? chartConfigOptions.yValueFormatter : undefined;

  if (!crosshair?.locked && model.opacity === 0) {
    if (isHeaderMode) {
      chartConfigOptions?.onHeaderContentChange?.(null);
    }

    hideFloatingTooltipElement(chart);
    return;
  }

  const syncedPoint = crosshair?.locked ? crosshair.syncedPoint : null;
  const dataPoints = model.dataPoints?.map(dataPoint =>
    syncedPoint && dataPoint.datasetIndex === syncedPoint.datasetIndex
      ? {
          ...dataPoint,
          parsed: { ...dataPoint.parsed, x: syncedPoint.x, y: syncedPoint.y },
          raw: { x: syncedPoint.x, y: syncedPoint.y },
          label: String(syncedPoint.x),
          formattedValue: String(syncedPoint.y),
        }
      : dataPoint,
  );

  if (!dataPoints) {
    if (isHeaderMode) {
      chartConfigOptions?.onHeaderContentChange?.(null);
    }

    hideFloatingTooltipElement(chart);
    return;
  }

  if (dataPoints) {
    const customContent = contentFormatter?.(dataPoints);
    const defaultTitleLines =
      unitsX === DEGREE_UNITS
        ? dataPoints
            .filter(dataPoint => dataPoint.raw !== 0)
            .map(dataPoint => {
              const reversedIndex = 360 - 1 - dataPoint.dataIndex;
              const formattedLabel = `${reversedIndex}${unitsX}`;
              return `${dataPoint.dataset.label}: ${formattedLabel}`;
            })
        : dataPoints.map(dataPoint => {
            const customTitle = titleFormatter?.(dataPoint);

            if (customTitle !== undefined) {
              return customTitle;
            }

            const isDate = DATE_TIME_LABEL_REGEX.test(dataPoint.label);

            if (isDate) {
              return `${dataPoint.dataset.label}: ${appendUnits(dataPoint.label, unitsX)}`;
            }

            const labelValue = getTooltipXValue(dataPoint, xOffset);

            if (labelValue === null) {
              return `${dataPoint.dataset.label}: ${appendUnits(dataPoint.label, unitsX)}`;
            }

            return `${dataPoint.dataset.label}: ${appendUnits(
              formatTooltipValue(labelValue, dataPoint, xValueFormatter),
              unitsX,
            )}`;
          }) || [];

    const defaultBodyLines =
      unitsX === DEGREE_UNITS
        ? dataPoints
            .filter(dataPoint => dataPoint.raw !== 0)
            .map(dataPoint => {
              const value = parseTooltipNumber(dataPoint.formattedValue);
              const formattedValue =
                value === null ? dataPoint.formattedValue : formatTooltipValue(value, dataPoint, yValueFormatter);
              return appendUnits(formattedValue, unitsY);
            })
        : dataPoints.map(dataPoint => {
            const isDate = DATE_LABEL_REGEX.test(dataPoint.label);
            const value = getTooltipYValue(dataPoint, yOffset, isDate);
            const yAxisID = isDate ? dataPoint.dataset.yAxisID : unitsY;
            const formattedValue =
              value === null ? dataPoint.formattedValue : formatTooltipValue(value, dataPoint, yValueFormatter);
            return appendUnits(formattedValue, yAxisID);
          });

    const baseContent: IExternalTooltipContent = {
      titleLines: customContent?.titleLines ?? defaultTitleLines,
      bodyLines: customContent?.bodyLines ?? defaultBodyLines,
    };
    const decoratedContent = contentDecorator?.(baseContent, dataPoints, chart) ?? baseContent;
    const titleLines = decoratedContent.titleLines ?? [];
    const bodyLines = (decoratedContent.bodyLines ?? []).map(normalizeBodyLine);

    if (
      !customContent &&
      unitsX === DEGREE_UNITS &&
      (bodyLines.length === 0 ||
        dataPoints
          .filter(dataPoint => dataPoint.raw !== 0)
          .every(dataPoint => (parseTooltipNumber(dataPoint.formattedValue) ?? 0) === 0))
    ) {
      if (isHeaderMode) {
        chartConfigOptions?.onHeaderContentChange?.(null);
      }

      hideFloatingTooltipElement(chart);
      return;
    }

    if (isHeaderMode) {
      chartConfigOptions?.onHeaderContentChange?.({ titleLines, bodyLines });
      hideFloatingTooltipElement(chart);
      return;
    }

    const tableHead = document.createElement("thead");

    titleLines.forEach(title => {
      const tr = document.createElement("tr");
      tr.style.borderWidth = "0";

      const th = document.createElement("th");
      th.style.borderWidth = "0";
      th.style.fontSize = "18px";
      th.style.textAlign = "left";
      const text = document.createTextNode(title);

      th.appendChild(text);
      tr.appendChild(th);
      tableHead.appendChild(tr);
    });

    const tableBody = document.createElement("tbody");
    bodyLines.forEach((body, i) => {
      const defaultColor = model.labelColors[i]?.borderColor as string | undefined;
      const lineColor = body.color === undefined ? defaultColor : body.color;

      const tr = document.createElement("tr");
      tr.style.backgroundColor = "inherit";
      tr.style.borderWidth = "0";

      const td = document.createElement("td");
      td.style.borderWidth = "0";
      td.style.textAlign = "left";

      if (lineColor) {
        const span = document.createElement("span");
        span.style.background = lineColor;
        span.style.borderColor = lineColor;
        span.style.borderRadius = "50px";
        span.style.borderWidth = "2px";
        span.style.marginRight = "10px";
        span.style.height = "10px";
        span.style.width = "10px";
        span.style.display = "inline-block";

        td.appendChild(span);
      }

      const text = document.createTextNode(body.text);

      td.appendChild(text);
      tr.appendChild(td);
      tableBody.appendChild(tr);
    });

    const tableRoot = tooltipEl.querySelector("table")!;

    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    tableRoot.appendChild(tableHead);
    tableRoot.appendChild(tableBody);
  }

  tooltipEl.style.opacity = "1";
  tooltipEl.style.display = "flex";
  tooltipEl.style.alignItems = "flex-start";
  tooltipEl.style.justifyContent = "flex-start";
  tooltipEl.style.padding = `${model.options.padding as number}px`;

  const tooltipRect = tooltipEl.getBoundingClientRect();

  const canvasRect = chart.canvas.getBoundingClientRect();
  const crosshairX = crosshair?.x;
  const useCrosshairPosition = crosshair?.enabled && crosshairX != null && Number.isFinite(crosshairX);
  const caretPoint =
    chart._externalTooltipPoint ??
    getViewportTooltipPoint(chart, useCrosshairPosition ? crosshairX : model.caretX, model.caretY);
  const cursorX = canvasRect.left + caretPoint.x;
  const cursorY = canvasRect.top + caretPoint.y;

  const { left, top, transform } = computeViewportTooltipPosition(
    cursorX,
    cursorY,
    tooltipRect.width,
    tooltipRect.height,
  );

  Object.assign(tooltipEl.style, {
    transform,
    left: `${left}px`,
    top: `${top}px`,
  });
};
